/**
 * Resource Detail Page - Full Resource Profile
 * 
 * Shows complete information about a research interpreter:
 * - Credentials, expertise, works
 * - Frameworks created by this resource
 * - How Cedar uses this resource
 * - Perspective and biases
 * - Chat integration
 * 
 * Example: Peter Attia detail page
 * 
 * Author: Coach Cedar Development Team
 * Date: December 11, 2025
 */

import React, { useState, useEffect } from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useResourceDetail } from '../hooks/useResearchLibrary';
import { storage } from '../services/storage';
import { ArrowLeft, Star, Book, Radio, FileText, MessageCircle, Target, ExternalLink, Youtube } from 'lucide-react';
import { ChatWithCedarIcon } from '../components/ChatWithCedarIcon';

export const ResourceDetail: React.FC = () => {
  const { resourceId } = useLocalSearchParams<{ resourceId: string }>();
  const router = useRouter();
  const { data, loading, error } = useResourceDetail(resourceId || null);
  const [researchPapers, setResearchPapers] = useState<any[]>([]);
  const [papersLoading, setPapersLoading] = useState(false);

  // Fetch research papers for researchers without frameworks
  useEffect(() => {
    if (data && data.frameworks.length === 0 && resourceId) {
      fetchResearchPapers();
    }
  }, [data, resourceId]);

  const fetchResearchPapers = async () => {
    setPapersLoading(true);
    try {
      const token = await storage.getItem('jwt_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      const response = await fetch(`/api/research-library/resource/${resourceId}/research-papers`, { headers });
      if (response.ok) {
        const paperData = await response.json();
        setResearchPapers(paperData.papers || []);
      }
    } catch (err) {
      console.error('Failed to fetch research papers:', err);
    } finally {
      setPapersLoading(false);
    }
  };

  const handleBack = () => {
    router.push('/insights?tab=resources');
  };

  const handleChatWithCedar = (topic?: string) => {
    const resource = data?.resource;
    if (!resource) return;

    router.push(`/chat?from=resource-detail&resourceId=${resource.id}&resourceName=${encodeURIComponent(resource.name)}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft size={20} />
          Back to Resources
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">{error || 'Resource not found'}</p>
        </div>
      </div>
    );
  }

  const { resource, frameworks } = data;

  const getPeerRecognitionStars = (score: number) => {
    const stars = Math.round(score * 5);
    return '⭐'.repeat(stars);
  };

  const hasAnyExternalLinks = (links: any): boolean => {
    if (!links || typeof links !== 'object') return false;
    return Object.values(links).some(link => link && typeof link === 'string' && link.length > 0);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft size={20} />
          Back to Resources
        </button>

        {/* REDESIGNED HEADER - Clean, Professional, Coach Cedar Branded */}
        <div className="mypath-card" style={{ minHeight: '140px', position: 'relative' }}>
          {/* White content overlay */}
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-start justify-between">
              {/* Left: Name + Credentials + Expertise Pills */}
              <div style={{ flex: 1 }}>
                <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--cedar-dark)' }}>
                  {resource.name}
                </h1>
                
                {/* Credentials */}
                {resource.credentials.length > 0 && (
                  <p className="text-base mb-3" style={{ color: 'var(--coloron-surface600)' }}>
                    {resource.credentials.join(', ')}
                  </p>
                )}
                
                {/* Expertise Pills */}
                {resource.primary_expertise.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {resource.primary_expertise.slice(0, 3).map((expertise, idx) => (
                      <span key={idx} className="px-3 py-1 text-xs font-medium rounded-full" style={{ 
                        backgroundColor: 'rgba(46, 94, 52, 0.1)',
                        color: 'var(--cedar-primary)'
                      }}>
                        {expertise.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Quality Indicators Row */}
                <div className="flex items-center gap-4 text-sm">
                  {/* Consensus Alignment */}
                  {resource.consensus_alignment_score > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium" style={{ color: 'var(--coloron-surface600)' }}>Consensus:</span>
                      <span className="font-semibold" style={{ color: 'var(--cedar-primary)' }}>
                        {(resource.consensus_alignment_score * 100).toFixed(0)}%
                      </span>
                    </div>
                  )}
                  
                  {/* Peer Recognition */}
                  {resource.peer_recognition_score > 0 && (
                    <div className="flex items-center gap-2">
                      <span style={{ color: '#F59E0B' }}>{getPeerRecognitionStars(resource.peer_recognition_score)}</span>
                      <span className="text-xs" style={{ color: 'var(--coloron-surface600)' }}>
                        ({(resource.peer_recognition_score * 100).toFixed(0)}% recognition)
                      </span>
                    </div>
                  )}
                  
                  {/* Framework Count */}
                  {frameworks.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Book size={14} style={{ color: 'var(--cedar-primary)' }} />
                      <span className="text-xs font-medium" style={{ color: 'var(--cedar-primary)' }}>
                        {frameworks.length} {frameworks.length === 1 ? 'Framework' : 'Frameworks'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Right: CC Chat Button */}
              <button
                onClick={() => handleChatWithCedar()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--cedar-primary)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cedar-dark)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cedar-primary)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                aria-label={`Chat with Cedar about ${resource.name}`}
              >
                <ChatWithCedarIcon 
                  size="medium"
                  variant="nav"
                  active={true}
                  style={{ width: '26px', height: '26px' }}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bio Section - PROMINENT with Coach Cedar styling */}
      {resource.bio && (
        <div className="bg-white rounded-lg border-2 p-6" style={{ borderColor: 'var(--cedar-limestone)' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ 
            color: 'var(--cedar-dark)',
            borderBottom: '2px solid var(--cedar-limestone)',
            paddingBottom: '8px'
          }}>
            About {resource.name}
          </h2>
          <div className="text-sm" style={{ 
            color: 'var(--coloron-surface700)',
            lineHeight: '1.8',
            whiteSpace: 'pre-wrap'
          }}>
            {resource.bio}
          </div>
        </div>
      )}

      {/* Notable Achievements - Enhanced styling */}
      {resource.notable_achievements && resource.notable_achievements.length > 0 && (
        <div className="bg-white rounded-lg border-2 p-6" style={{ borderColor: 'var(--cedar-limestone)' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ 
            color: 'var(--cedar-dark)',
            borderBottom: '2px solid var(--cedar-limestone)',
            paddingBottom: '8px'
          }}>
            Notable Achievements
          </h2>
          <ul style={{ 
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {resource.notable_achievements.map((achievement, idx) => (
              <li key={idx} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '12px',
                backgroundColor: 'var(--cedar-subtle)',
                borderRadius: '8px',
                borderLeft: '3px solid var(--cedar-primary)'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--cedar-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <span style={{ 
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    ✓
                  </span>
                </div>
                <span className="text-sm" style={{ 
                  color: 'var(--coloron-surface900)',
                  lineHeight: '1.6',
                  flex: 1
                }}>
                  {achievement}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Primary Expertise - Enhanced with better styling */}
      {resource.primary_expertise.length > 0 && (
        <div className="bg-white rounded-lg border-2 p-6" style={{ borderColor: 'var(--cedar-limestone)' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ 
            color: 'var(--cedar-dark)',
            borderBottom: '2px solid var(--cedar-limestone)',
            paddingBottom: '8px'
          }}>
            Primary Expertise
          </h2>
          <div className="flex flex-wrap gap-2">
            {resource.primary_expertise.map((expertise, idx) => (
              <span key={idx} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ 
                backgroundColor: 'var(--cedar-subtle)',
                color: 'var(--cedar-dark)',
                border: '1px solid var(--cedar-limestone)'
              }}>
                {expertise.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Primary Works - ENHANCED with better styling */}
      {resource.primary_works && resource.primary_works.length > 0 && (
        <div className="bg-white rounded-lg border-2 p-6" style={{ borderColor: 'var(--cedar-limestone)' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ 
            color: 'var(--cedar-dark)',
            borderBottom: '2px solid var(--cedar-limestone)',
            paddingBottom: '8px'
          }}>
            Primary Works
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {resource.primary_works.map((work: any, idx: number) => {
              const getWorkIconComponent = (type: string) => {
                switch(type) {
                  case 'book': return <Book size={24} style={{ color: 'var(--cedar-primary)' }} />;
                  case 'podcast': return <Radio size={24} style={{ color: 'var(--cedar-primary)' }} />;
                  case 'video': return <Youtube size={24} style={{ color: 'var(--cedar-primary)' }} />;
                  default: return <FileText size={24} style={{ color: 'var(--cedar-primary)' }} />;
                }
              };

              return (
                <div key={idx} style={{
                  padding: '20px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '2px solid var(--cedar-limestone)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '16px'
                  }}>
                    <div style={{ 
                      marginTop: '4px',
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: 'var(--cedar-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {getWorkIconComponent(work.type)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        fontSize: '17px',
                        fontWeight: '600',
                        color: 'var(--cedar-dark)',
                        marginBottom: '6px',
                        lineHeight: '1.4'
                      }}>
                        {work.title}
                      </h3>
                      {(work.year || work.edition) && (
                        <p className="text-sm" style={{ 
                          color: 'var(--coloron-surface600)',
                          marginBottom: '10px'
                        }}>
                          {work.edition && `${work.edition} edition`}{work.edition && work.year && ' • '}{work.year}
                        </p>
                      )}
                      {work.description && (
                        <p className="text-sm" style={{ 
                          color: 'var(--coloron-surface700)',
                          lineHeight: '1.7',
                          marginBottom: '12px'
                        }}>
                          {work.description}
                        </p>
                      )}
                      {work.url && (
                        <a
                          href={work.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                          style={{
                            backgroundColor: 'var(--cedar-primary)',
                            color: 'white',
                            textDecoration: 'none'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-dark)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-primary)'}
                        >
                          {work.type === 'book' ? 'View on Amazon' : 
                           work.type === 'podcast' ? 'Listen to Podcast' : 
                           work.type === 'video' ? 'Watch on YouTube' : 
                           work.type === 'article' ? 'View Research' : 
                           'Learn More'}
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* How This Resource Informs Your Training - TIGHTER, MORE BRANDED */}
      <div className="rounded-lg p-6" style={{ 
        background: 'linear-gradient(135deg, rgba(66, 165, 165, 0.15), rgba(31, 108, 79, 0.1))',
        border: '2px solid var(--cedar-primary)',
        boxShadow: '0 4px 12px rgba(31, 108, 79, 0.08)'
      }}>
        <div className="flex items-center gap-3 mb-4">
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: 'var(--cedar-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Target size={20} style={{ color: 'white' }} />
          </div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--cedar-dark)' }}>
            How This Shapes Your Training
          </h2>
        </div>
        
        {/* VARIANT 1: Has frameworks */}
        {frameworks.length > 0 && (
          <div className="space-y-3">
            {frameworks.length === 1 && (
              <div className="bg-white rounded-lg p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <p className="text-sm font-medium mb-2" style={{ color: 'var(--cedar-dark)' }}>
                  <strong>{frameworks[0].framework_name}</strong>
                </p>
                <p className="text-sm" style={{ color: 'var(--coloron-surface700)', lineHeight: '1.7' }}>
                  Directly structures your weekly plans through <strong>{frameworks[0].practical_guidelines?.length || 0} practical guidelines</strong> and <strong>{frameworks[0].key_principles?.length || 0} core principles</strong>. Cedar applies these protocols for volume targets, exercise selection, and progression.
                </p>
              </div>
            )}
            {frameworks.length > 1 && (
              <div className="bg-white rounded-lg p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <p className="text-sm" style={{ color: 'var(--coloron-surface700)', lineHeight: '1.7' }}>
                  Cedar integrates <strong>{frameworks.length} frameworks</strong>, selecting the most relevant protocols based on your goals, experience, and training phase.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {frameworks.map(fw => (
                    <span key={fw.id} className="px-2 py-1 text-xs font-medium rounded" style={{ 
                      backgroundColor: 'rgba(46, 94, 52, 0.1)',
                      color: 'var(--cedar-primary)'
                    }}>
                      {fw.framework_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* VARIANT 2: No frameworks but has papers */}
        {frameworks.length === 0 && researchPapers.length > 0 && (
          <div className="bg-white rounded-lg p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <p className="text-sm mb-3" style={{ color: 'var(--coloron-surface700)', lineHeight: '1.7' }}>
              <strong>{researchPapers.length} peer-reviewed papers</strong> on {resource.primary_expertise.slice(0, 2).join(' and ')} 
              inform Cedar's intelligence engines when analyzing your training and providing evidence-based recommendations.
            </p>
            <div className="text-xs px-3 py-2 rounded" style={{ 
              backgroundColor: 'rgba(66, 165, 165, 0.1)',
              color: 'var(--coloron-surface700)'
            }}>
              📚 Research foundation for {resource.primary_expertise.slice(0, 2).join(', ')} guidance
            </div>
          </div>
        )}
      </div>

      {/* Research Papers Section - Only for researchers without frameworks */}
      {frameworks.length === 0 && (
        <div className="bg-white rounded-lg border-2 p-6" style={{ borderColor: 'var(--cedar-limestone)' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ 
            color: 'var(--cedar-dark)',
            borderBottom: '2px solid var(--cedar-limestone)',
            paddingBottom: '8px'
          }}>
            Peer-Reviewed Research
          </h2>
          
          {/* Show actual papers from research_sources table */}
          {papersLoading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
            </div>
          )}
          
          {!papersLoading && researchPapers.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 className="text-sm font-semibold mb-3" style={{ 
                color: 'var(--coloron-surface600)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Papers in Coach Cedar's Research Graph ({researchPapers.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {researchPapers.slice(0, 5).map((paper: any) => (
                  <div key={paper.id} style={{
                    padding: '18px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '2px solid var(--cedar-limestone)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                  }}>
                    <h4 className="text-sm font-bold mb-2" style={{ 
                      color: 'var(--cedar-dark)',
                      lineHeight: '1.5'
                    }}>
                      {paper.title}
                    </h4>
                    <p className="text-xs mb-3" style={{ color: 'var(--coloron-surface600)' }}>
                      {paper.journal} • {paper.publication_year}
                    </p>
                    
                    {/* Key Findings */}
                    {paper.key_findings && paper.key_findings.length > 0 && (
                      <div className="rounded-lg p-3 mb-3" style={{ backgroundColor: 'var(--cedar-subtle)' }}>
                        <div className="text-xs font-bold mb-2" style={{ 
                          color: 'var(--cedar-dark)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Key Findings:
                        </div>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                          {paper.key_findings.slice(0, 2).map((finding: string, idx: number) => (
                            <li key={idx} className="text-xs pl-4 relative" style={{ 
                              color: 'var(--coloron-surface900)',
                              lineHeight: '1.6',
                              marginBottom: '6px'
                            }}>
                              <span className="absolute left-0 font-bold" style={{ color: 'var(--cedar-primary)' }}>•</span>
                              {finding}
                            </li>
                          ))}
                        </ul>
                        {paper.key_findings.length > 2 && (
                          <span className="text-xs" style={{ color: 'var(--coloron-surface600)' }}>
                            +{paper.key_findings.length - 2} more findings
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Quality indicators */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '12px', 
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {paper.evidence_strength && (
                        <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{
                          backgroundColor: paper.evidence_strength === 'strong' ? 'rgba(46, 94, 52, 0.15)' : 
                                         paper.evidence_strength === 'moderate' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(66, 165, 165, 0.15)',
                          color: paper.evidence_strength === 'strong' ? 'var(--cedar-primary)' : 
                                 paper.evidence_strength === 'moderate' ? '#D97706' : '#0E9AA0'
                        }}>
                          {paper.evidence_strength}
                        </span>
                      )}
                      {paper.quality_score && (
                        <span className="text-xs font-medium" style={{ color: 'var(--coloron-surface600)' }}>
                          Quality: {paper.quality_score.toFixed(0)}/100
                        </span>
                      )}
                      {paper.doi && (
                        <a
                          href={`https://doi.org/${paper.doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium inline-flex items-center gap-1 ml-auto"
                          style={{
                            color: 'var(--cedar-primary)',
                            textDecoration: 'none'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                        >
                          View Paper <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {researchPapers.length > 5 && (
                  <p className="text-xs text-center" style={{ color: 'var(--coloron-surface500)' }}>
                    +{researchPapers.length - 5} more papers available
                  </p>
                )}
              </div>
            </div>
          )}
          
          <div style={{
            padding: '12px',
            backgroundColor: '#F0F9FF',
            borderRadius: '6px',
            border: '1px solid #BAE6FD'
          }}>
            <p className="text-xs" style={{ 
              color: 'var(--coloron-surface600)',
              lineHeight: '1.5'
            }}>
              💡 <strong>How Cedar uses this research:</strong> When analyzing your training, {resource.name}'s findings on {resource.primary_expertise[0]?.replace(/_/g, ' ')} validate recommendations, inform progression rates, and ensure evidence-based coaching. Their work is cited in intelligence analyses and plan generation.
            </p>
          </div>
        </div>
      )}

      {/* Frameworks - Enhanced styling */}
      {frameworks.length > 0 && (
        <div className="bg-white rounded-lg border-2 p-6" style={{ borderColor: 'var(--cedar-limestone)' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ 
            color: 'var(--cedar-dark)',
            borderBottom: '2px solid var(--cedar-limestone)',
            paddingBottom: '8px'
          }}>
            {resource.name}'s Frameworks ({frameworks.length})
          </h2>
          <div className="space-y-4">
            {frameworks.map((framework) => (
              <FrameworkCard
                key={framework.id}
                framework={framework}
                onChatClick={() => handleChatWithCedar(framework.framework_name)}
              />
            ))}
          </div>
        </div>
      )}

      {/* External Links - ONLY show if we have actual links */}
      {hasAnyExternalLinks(resource.external_links) && (
        <div className="bg-white rounded-lg border-2 p-6" style={{ borderColor: 'var(--cedar-limestone)' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ 
            color: 'var(--cedar-dark)',
            borderBottom: '2px solid var(--cedar-limestone)',
            paddingBottom: '8px'
          }}>
            Connect & Learn More
          </h2>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '12px'
          }}>
            {resource.external_links.website && (
              <a
                href={resource.external_links.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--cedar-subtle)',
                  color: 'var(--cedar-dark)',
                  textDecoration: 'none',
                  border: '1px solid var(--cedar-limestone)',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(46, 94, 52, 0.15)';
                  e.currentTarget.style.borderColor = 'var(--cedar-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)';
                  e.currentTarget.style.borderColor = 'var(--cedar-limestone)';
                }}
              >
                <ExternalLink size={16} />
                Website
              </a>
            )}
            
            {resource.external_links.podcast && (
              <a
                href={resource.external_links.podcast}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--cedar-subtle)',
                  color: 'var(--cedar-dark)',
                  textDecoration: 'none',
                  border: '1px solid var(--cedar-limestone)',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(46, 94, 52, 0.15)';
                  e.currentTarget.style.borderColor = 'var(--cedar-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)';
                  e.currentTarget.style.borderColor = 'var(--cedar-limestone)';
                }}
              >
                <Radio size={16} />
                Podcast
              </a>
            )}
            
            {resource.external_links.youtube && (
              <a
                href={resource.external_links.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--cedar-subtle)',
                  color: 'var(--cedar-dark)',
                  textDecoration: 'none',
                  border: '1px solid var(--cedar-limestone)',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(46, 94, 52, 0.15)';
                  e.currentTarget.style.borderColor = 'var(--cedar-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)';
                  e.currentTarget.style.borderColor = 'var(--cedar-limestone)';
                }}
              >
                <Youtube size={16} />
                YouTube
              </a>
            )}
            
            {resource.external_links.amazon && (
              <a
                href={resource.external_links.amazon}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--cedar-subtle)',
                  color: 'var(--cedar-dark)',
                  textDecoration: 'none',
                  border: '1px solid var(--cedar-limestone)',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(46, 94, 52, 0.15)';
                  e.currentTarget.style.borderColor = 'var(--cedar-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)';
                  e.currentTarget.style.borderColor = 'var(--cedar-limestone)';
                }}
              >
                <Book size={16} />
                Amazon
              </a>
            )}
            
            {resource.external_links.instagram && (
              <a
                href={resource.external_links.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--cedar-subtle)',
                  color: 'var(--cedar-dark)',
                  textDecoration: 'none',
                  border: '1px solid var(--cedar-limestone)',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(46, 94, 52, 0.15)';
                  e.currentTarget.style.borderColor = 'var(--cedar-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)';
                  e.currentTarget.style.borderColor = 'var(--cedar-limestone)';
                }}
              >
                <Target size={16} />
                Instagram
              </a>
            )}
            
            {resource.external_links.twitter && (
              <a
                href={resource.external_links.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--cedar-subtle)',
                  color: 'var(--cedar-dark)',
                  textDecoration: 'none',
                  border: '1px solid var(--cedar-limestone)',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(46, 94, 52, 0.15)';
                  e.currentTarget.style.borderColor = 'var(--cedar-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)';
                  e.currentTarget.style.borderColor = 'var(--cedar-limestone)';
                }}
              >
                <MessageCircle size={16} />
                Twitter
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// FRAMEWORK CARD (WITHIN RESOURCE DETAIL)
// ============================================

interface FrameworkCardProps {
  framework: any;
  onChatClick: () => void;
}

const FrameworkCard: React.FC<FrameworkCardProps> = ({ framework, onChatClick }) => {
  const [expanded, setExpanded] = useState(false);

  const getEvidenceBadge = (basis: string) => {
    const styles = {
      research_backed: { bg: 'rgba(46, 94, 52, 0.15)', color: 'var(--cedar-primary)', label: 'research backed' },
      experience_based: { bg: 'rgba(66, 165, 165, 0.15)', color: '#0E9AA0', label: 'experience based' },
      theoretical: { bg: 'rgba(120, 52, 181, 0.15)', color: '#7834B5', label: 'theoretical' },
      mixed: { bg: 'rgba(245, 158, 11, 0.15)', color: '#D97706', label: 'mixed evidence' }
    };
    return styles[basis as keyof typeof styles] || styles.mixed;
  };

  const evidenceStyle = getEvidenceBadge(framework.evidence_basis);

  return (
    <div className="rounded-lg p-5" style={{
      border: '2px solid var(--cedar-limestone)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      backgroundColor: 'white'
    }}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-1" style={{ color: 'var(--cedar-dark)' }}>
            {framework.framework_name}
          </h3>
          {framework.source_work && (
            <p className="text-sm" style={{ color: 'var(--coloron-surface600)' }}>
              From: {framework.source_work}
            </p>
          )}
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{
          backgroundColor: evidenceStyle.bg,
          color: evidenceStyle.color
        }}>
          {evidenceStyle.label}
        </span>
      </div>

      {framework.description && (
        <p className="text-sm mb-4" style={{ 
          color: 'var(--coloron-surface700)',
          lineHeight: '1.7'
        }}>
          {framework.description}
        </p>
      )}

      {expanded && (
        <div className="space-y-4 mb-4">
          {framework.key_principles && framework.key_principles.length > 0 && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--cedar-subtle)' }}>
              <h4 className="text-xs font-bold uppercase mb-3" style={{ 
                color: 'var(--cedar-dark)',
                letterSpacing: '0.5px'
              }}>
                Key Principles
              </h4>
              <ul className="space-y-2">
                {framework.key_principles.map((principle: string, idx: number) => (
                  <li key={idx} className="text-sm pl-4 relative" style={{ 
                    color: 'var(--coloron-surface900)',
                    lineHeight: '1.6'
                  }}>
                    <span className="absolute left-0 font-bold" style={{ color: 'var(--cedar-primary)' }}>•</span>
                    {principle}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {framework.practical_guidelines && framework.practical_guidelines.length > 0 && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(66, 165, 165, 0.08)' }}>
              <h4 className="text-xs font-bold uppercase mb-3" style={{ 
                color: 'var(--cedar-dark)',
                letterSpacing: '0.5px'
              }}>
                Practical Guidelines
              </h4>
              <ul className="space-y-2">
                {framework.practical_guidelines.map((guideline: string, idx: number) => (
                  <li key={idx} className="text-sm pl-4 relative" style={{ 
                    color: 'var(--coloron-surface900)',
                    lineHeight: '1.6'
                  }}>
                    <span className="absolute left-0 font-bold" style={{ color: 'var(--cedar-primary)' }}>•</span>
                    {guideline}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-3" style={{ borderTop: '1px solid var(--cedar-limestone)' }}>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: 'var(--cedar-subtle)',
            color: 'var(--cedar-dark)',
            border: 'none'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(46, 94, 52, 0.15)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)'}
        >
          {expanded ? 'Show Less' : 'Show Details'}
        </button>
        <button
          onClick={onChatClick}
          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: 'var(--cedar-primary)',
            color: 'white',
            border: 'none'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-dark)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-primary)'}
        >
          <MessageCircle size={14} />
          Chat
        </button>
      </div>
    </div>
  );
};
